import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { MessageCircle, Mic, Heart, CheckCircle, AlertCircle, User } from 'lucide-react'
import { MultiTypeAssessment } from '../../components/assessments/MultiTypeAssessment'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { db } from '../../lib/supabase'
import toast from 'react-hot-toast'

export const EmployeeAssessment: React.FC = () => {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [employee, setEmployee] = useState<any>(null)
  const [tokenValid, setTokenValid] = useState(false)
  const [assessmentComplete, setAssessmentComplete] = useState(false)
  const [results, setResults] = useState<any>(null)

  useEffect(() => {
    if (token) {
      validateToken()
    }
  }, [token])

  const validateToken = async () => {
    try {
      setLoading(true)
      const { data: tokenData, error } = await db.assessmentTokens.getByToken(token!)
      
      if (error || !tokenData) {
        toast.error('Invalid or expired assessment link')
        setTokenValid(false)
        return
      }

      setEmployee(tokenData.employees)
      setTokenValid(true)
      toast.success(`Welcome ${tokenData.employees.first_name}! Ready to start your wellness assessment.`)
    } catch (error) {
      console.error('Error validating token:', error)
      toast.error('Failed to validate assessment link')
      setTokenValid(false)
    } finally {
      setLoading(false)
    }
  }

  const handleAssessmentComplete = async (assessmentResults: any) => {
    try {
      // Mark token as used
      await db.assessmentTokens.markAsUsed(token!)

      // Save assessment results
      const { error } = await db.assessments.create({
        employee_id: employee.id,
        company_id: employee.company_id,
        assessment_type: assessmentResults.assessment_type || 'multi_type',
        conversation_data: assessmentResults.conversation_data || assessmentResults,
        overall_metrics: assessmentResults.overall_metrics,
        insights: assessmentResults.ai_insights || assessmentResults.insights,
        recommendations: assessmentResults.recommendations,
        completed_at: new Date().toISOString()
      })

      if (error) throw error

      setResults(assessmentResults)
      setAssessmentComplete(true)
      toast.success('Assessment completed and saved successfully!')
    } catch (error) {
      console.error('Error saving assessment:', error)
      toast.error('Failed to save assessment results')
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-calming flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sage-500 mx-auto mb-4"></div>
          <p className="text-slate-600">Validating assessment link...</p>
        </div>
      </div>
    )
  }

  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-calming flex items-center justify-center">
        <Card className="p-8 max-w-md mx-auto text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">
            Invalid Assessment Link
          </h1>
          <p className="text-slate-600 mb-6">
            This assessment link is invalid, expired, or has already been used.
          </p>
          <Button
            onClick={() => navigate('/')}
            className="bg-sage-600 hover:bg-sage-700 text-white"
          >
            Go to Homepage
          </Button>
        </Card>
      </div>
    )
  }

  if (assessmentComplete && results) {
    return (
      <div className="min-h-screen bg-calming">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <Card className="p-8">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              </div>
              
              <h1 className="text-3xl font-bold text-slate-900 mb-4">
                Thank You, {employee.first_name}!
              </h1>
              
              <p className="text-lg text-slate-600 mb-8">
                Your wellness assessment has been completed and submitted to your HR team.
              </p>

              {/* Assessment Summary */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-sage-50 rounded-lg p-6">
                  <div className="text-2xl font-bold text-sage-700 mb-2">
                    {Math.round((results.overall_metrics?.sentiment_score || 0.5) * 100)}%
                  </div>
                  <div className="text-sm text-sage-600">Overall Sentiment</div>
                </div>
                
                <div className="bg-blue-50 rounded-lg p-6">
                  <div className="text-2xl font-bold text-blue-700 mb-2">
                    {Math.round((results.overall_metrics?.engagement_level || 0.5) * 100)}%
                  </div>
                  <div className="text-sm text-blue-600">Engagement Level</div>
                </div>

                <div className="bg-emerald-50 rounded-lg p-6">
                  <div className="text-2xl font-bold text-emerald-700 mb-2">
                    {results.conversation_data?.conversation_turns?.length || 
                     results.responses?.length || 'N/A'}
                  </div>
                  <div className="text-sm text-emerald-600">Responses</div>
                </div>
              </div>

              {/* Show Report Button */}
              <Button
                onClick={() => {
                  // Generate and show detailed report
                  const reportWindow = window.open('', '_blank')
                  if (reportWindow) {
                    reportWindow.document.write(`
                      <html>
                        <head>
                          <title>Wellness Assessment Report - ${employee.first_name} ${employee.last_name}</title>
                          <style>
                            body { font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 20px; }
                            .header { text-align: center; margin-bottom: 30px; }
                            .metric { background: #f8f9fa; padding: 15px; margin: 10px 0; border-radius: 8px; }
                            .recommendations { background: #e8f5e8; padding: 15px; border-radius: 8px; }
                          </style>
                        </head>
                        <body>
                          <div class="header">
                            <h1>Wellness Assessment Report</h1>
                            <h2>${employee.first_name} ${employee.last_name}</h2>
                            <p>Department: ${employee.department || 'Not specified'}</p>
                            <p>Assessment Date: ${new Date().toLocaleDateString()}</p>
                          </div>
                          
                          <div class="metric">
                            <h3>Overall Sentiment: ${Math.round((results.overall_metrics?.sentiment_score || 0.5) * 100)}%</h3>
                            <p>This indicates the general emotional tone of the assessment.</p>
                          </div>
                          
                          <div class="metric">
                            <h3>Engagement Level: ${Math.round((results.overall_metrics?.engagement_level || 0.5) * 100)}%</h3>
                            <p>This measures how actively the employee participated in the assessment.</p>
                          </div>
                          
                          ${results.recommendations && results.recommendations.length > 0 ? `
                            <div class="recommendations">
                              <h3>Recommendations:</h3>
                              <ul>
                                ${results.recommendations.map((rec: string) => `<li>${rec}</li>`).join('')}
                              </ul>
                            </div>
                          ` : ''}
                          
                          <p><em>This report is confidential and should be handled according to your company's privacy policies.</em></p>
                        </body>
                      </html>
                    `)
                    reportWindow.document.close()
                  }
                }}
                className="bg-sage-600 hover:bg-sage-700 text-white px-8 py-3"
                size="lg"
              >
                Show Detailed Report
              </Button>

              <div className="mt-8 text-sm text-slate-500">
                <p>Your responses are confidential and will only be shared with authorized HR personnel.</p>
              </div>
            </Card>
          </motion.div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-calming">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="w-16 h-16 bg-sage-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <User className="w-8 h-8 text-sage-600" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">
            Welcome, {employee.first_name}!
          </h1>
          <p className="text-lg text-slate-600 mb-4">
            You've been invited to complete a wellness assessment
          </p>
          <div className="text-sm text-slate-500">
            <p>Department: {employee.department || 'Not specified'}</p>
            <p>This assessment is confidential and will help your team better support your wellbeing</p>
          </div>
        </motion.div>

        {/* Assessment */}
        <MultiTypeAssessment
          employeeId={employee.id}
          onComplete={handleAssessmentComplete}
        />
      </div>
    </div>
  )
}